"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.roundDecimalProduct = roundDecimalProduct;
exports.sumOfProducts = sumOfProducts;
exports.roundHalfEven = roundHalfEven;
exports.clampAndSnapToStep = clampAndSnapToStep;
exports.computeMonthlyPaymentCents = computeMonthlyPaymentCents;
exports.computeSliderMonthlyPaymentCents = computeSliderMonthlyPaymentCents;
/** 10^exponent as a bigint. Built from digits, not `**`: notary's transpiler turns `**` into `Math.pow`, which throws on bigints. */
function powerOfTen(exponent) {
    return BigInt(`1${'0'.repeat(exponent)}`);
}
function decimalFraction(value) {
    const text = value.toString().toLowerCase();
    const [coefficient, exponentText] = text.split('e');
    const exponent = exponentText ? Number(exponentText) : 0;
    const [whole, fraction = ''] = coefficient.split('.');
    const digits = `${whole}${fraction}`;
    let numerator = BigInt(digits);
    let denominator = powerOfTen(fraction.length);
    if (exponent > 0) {
        numerator *= powerOfTen(exponent);
    }
    if (exponent < 0) {
        denominator *= powerOfTen(-exponent);
    }
    return { numerator, denominator };
}
/** Round two finite decimal values to the nearest integer without binary floating-point arithmetic. */
function roundDecimalProduct(amount, ratio) {
    const amountFraction = decimalFraction(amount);
    const ratioFraction = decimalFraction(ratio);
    const product = amountFraction.numerator * ratioFraction.numerator;
    const denominator = amountFraction.denominator * ratioFraction.denominator;
    const sign = product < 0n ? -1n : 1n;
    const absoluteProduct = product < 0n ? -product : product;
    const rounded = (absoluteProduct * 2n + denominator) / (2n * denominator);
    return Number(sign * rounded);
}
/** The exact sum of decimal products, each term the product of its factors. */
function sumOfProducts(products) {
    let numerator = 0n;
    let denominator = 1n;
    for (const factors of products) {
        let productNumerator = 1n;
        let productDenominator = 1n;
        for (const factor of factors) {
            const fraction = decimalFraction(factor);
            productNumerator *= fraction.numerator;
            productDenominator *= fraction.denominator;
        }
        numerator = numerator * productDenominator + productNumerator * denominator;
        denominator *= productDenominator;
    }
    return { numerator, denominator };
}
/** Round to the nearest integer, ties to even: Python's Decimal quantize(ROUND_HALF_EVEN). */
function roundHalfEven({ numerator, denominator }) {
    const sign = numerator < 0n ? -1n : 1n;
    const absoluteNumerator = numerator < 0n ? -numerator : numerator;
    let quotient = absoluteNumerator / denominator;
    const twiceRemainder = 2n * (absoluteNumerator % denominator);
    if (twiceRemainder > denominator || (twiceRemainder === denominator && quotient % 2n === 1n)) {
        quotient += 1n;
    }
    return Number(sign * quotient);
}
/**
 * Snap a slider position onto a round step grid, keeping the two ends exactly reachable.
 *
 * The ends are the offer's own bounds and need not sit on the grid, so they pass through unrounded — only
 * intermediate positions snap, which is what lets a slider land on 95,000 / 100,000 rather than a
 * bound-relative value like 92,500. Unit-agnostic: pass all four arguments in the same unit (cents or
 * dollars). Values outside the bounds clamp to the nearer end.
 */
function clampAndSnapToStep({ value, minValue, maxValue, step }) {
    if (value <= minValue) {
        return minValue;
    }
    if (value >= maxValue) {
        return maxValue;
    }
    const snappedToGrid = Math.round(value / step) * step;
    return Math.min(maxValue, Math.max(minValue, snappedToGrid));
}
/** Standard amortization formula — monthly payment for a fixed-rate fully-amortizing loan. */
function computeMonthlyPaymentCents(principalCents, annualRateDecimal, termMonths) {
    if (termMonths <= 0) {
        return 0;
    }
    if (annualRateDecimal === 0) {
        return Math.round(principalCents / termMonths);
    }
    const r = annualRateDecimal / 12;
    const factor = Math.pow(1 + r, termMonths);
    return Math.round((principalCents * (r * factor)) / (factor - 1));
}
/**
 * Slider fixed-term payments use the drawn principal after recording fees are
 * excluded from the amortizing balance.
 */
function computeSliderMonthlyPaymentCents({ lineSizeCents, recordingFeeCents, annualRateDecimal, termMonths, }) {
    return computeMonthlyPaymentCents(lineSizeCents - recordingFeeCents, annualRateDecimal, termMonths);
}
